// useScopeSync
//
// Glue between the per-page selection state (selectedFacilityIds + the
// activeCustomerId / activeFilterId dropdowns) and the global ScopeContext
// that drives the StickyScopeBar.
//
// Responsibilities:
//   1. On mount and when scope changes externally, push the scope's
//      selection into local state (resolving customer → facility ids).
//   2. Auto-trigger the page's own loadData() the first time a scope is
//      applied, so users coming from a shortcut see results without
//      another click.
//   3. Watch URL params (?customerId / ?filterId / ?facilityId) and
//      promote them into context (so reload + sticky-bar still work).
//   4. Provide setScopeFromCustomer / setScopeFromFilter / clearScope
//      helpers the page calls from its in-page dropdowns so the sticky
//      bar mirrors the user's selection.
//
// The page still owns its dropdowns and `Load Data` button — this hook
// just keeps the global scope and the local selection in lock-step.

import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScope } from '../contexts/ScopeContext';

export function useScopeSync({
  customers,
  savedFilters,
  resolveCustomerFacilityIds,
  setSelectedFacilityIds,
  setActiveCustomerId,
  setActiveFilterId,
  loadData,
  ready, // true once the page is ready to load (e.g. dates set)
}) {
  const { scope, setScope, clearScope } = useScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const customerIdParam = searchParams.get('customerId');
  const filterIdParam   = searchParams.get('filterId');
  const facilityIdParam = searchParams.get('facilityId');

  // Tracks the last scope key we applied + auto-loaded so we don't loop or
  // re-fire when the user tweaks other state.
  const appliedKeyRef = useRef(null);

  // 1. Promote URL params into the global scope on mount. This handles the
  //    "user clicked a shortcut from My Customers" flow. When the param's
  //    label can be resolved (customer name / filter name / facility id),
  //    pass it along so the sticky bar shows something readable.
  useEffect(() => {
    if (customerIdParam) {
      const c = customers?.find(x => String(x.id) === String(customerIdParam));
      setScope({ kind: 'customer', id: customerIdParam, label: c?.name || `Customer #${customerIdParam}` });
    } else if (filterIdParam) {
      const sf = savedFilters?.find(x => String(x.id) === String(filterIdParam));
      setScope({ kind: 'filter', id: filterIdParam, label: sf?.name || `Filter #${filterIdParam}` });
    } else if (facilityIdParam) {
      // Facility shortcut links pass the canonical VE id; the human label
      // comes from the All Facilities row, but it isn't in the URL. Fall
      // back to "Facility N" — the user can hover the chip for the id.
      setScope({ kind: 'facility', id: facilityIdParam, label: `Facility ${facilityIdParam}` });
    }
    // Strip the URL params after promoting so refreshes use sessionStorage
    // (single source of truth) and the URL doesn't keep re-firing this on
    // re-render.
    if (customerIdParam || filterIdParam || facilityIdParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('customerId');
      next.delete('filterId');
      next.delete('facilityId');
      next.delete('groupId'); // legacy alias, drop too
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerIdParam, filterIdParam, facilityIdParam, customers, savedFilters]);

  // 2. Re-resolve labels into the scope object as soon as customers /
  //    savedFilters have loaded (the URL effect above runs before the
  //    customer list comes back, so labels start as "Customer #9" and
  //    upgrade once the list arrives).
  useEffect(() => {
    if (!scope || !scope.label?.startsWith('Customer #')) return;
    const c = customers?.find(x => String(x.id) === String(scope.id));
    if (c?.name) setScope({ ...scope, label: c.name });
  }, [customers, scope, setScope]);
  useEffect(() => {
    if (!scope || !scope.label?.startsWith('Filter #')) return;
    const sf = savedFilters?.find(x => String(x.id) === String(scope.id));
    if (sf?.name) setScope({ ...scope, label: sf.name });
  }, [savedFilters, scope, setScope]);

  // 3. Whenever the global scope changes (mount, sticky-bar X clear, or
  //    another page mutated it), reflect into local page state and
  //    auto-load on first apply of each unique scope.
  useEffect(() => {
    if (!ready) return;

    // If the URL still has scope params, effect 1 above is about to
    // promote them into context — wait for that next render so we don't
    // apply the stale sessionStorage scope and then immediately re-apply
    // the URL one.
    if (customerIdParam || filterIdParam || facilityIdParam) return;

    const key = scope ? `${scope.kind}:${scope.id}` : null;
    if (appliedKeyRef.current === key) return;

    // Filter scopes need the savedFilters list to resolve their facility
    // IDs. If it hasn't arrived yet, bail without marking as applied so
    // we re-run when savedFilters lands.
    if (scope?.kind === 'filter') {
      const sf = savedFilters?.find(x => String(x.id) === String(scope.id));
      if (!sf) return;
    }

    appliedKeyRef.current = key;

    (async () => {
      if (!scope) {
        setSelectedFacilityIds(new Set());
        setActiveCustomerId('');
        setActiveFilterId('');
        return;
      }

      if (scope.kind === 'customer') {
        const ids = await resolveCustomerFacilityIds(scope.id);
        setActiveCustomerId(scope.id);
        setActiveFilterId('');
        setSelectedFacilityIds(new Set(ids));
      } else if (scope.kind === 'filter') {
        const sf = savedFilters.find(x => String(x.id) === String(scope.id));
        setActiveFilterId(String(sf.id));
        setActiveCustomerId('');
        setSelectedFacilityIds(new Set(sf.facilityIds || []));
      } else if (scope.kind === 'facility') {
        setActiveCustomerId('');
        setActiveFilterId('');
        setSelectedFacilityIds(new Set([String(scope.id)]));
      }

      if (loadData) await loadData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, ready, savedFilters]);

  // 4. Helpers the page passes to its dropdown handlers. Calling these
  //    keeps the sticky bar mirrored without surprising the user — pick
  //    a customer in the dropdown, the chip updates; clear it, the chip
  //    disappears.
  const setScopeFromCustomer = useCallback((customerId) => {
    if (!customerId) { clearScope(); return; }
    const c = customers?.find(x => String(x.id) === String(customerId));
    setScope({ kind: 'customer', id: customerId, label: c?.name || `Customer #${customerId}` });
  }, [customers, setScope, clearScope]);

  const setScopeFromFilter = useCallback((filterId) => {
    if (!filterId) { clearScope(); return; }
    const sf = savedFilters?.find(x => String(x.id) === String(filterId));
    setScope({ kind: 'filter', id: filterId, label: sf?.name || `Filter #${filterId}` });
  }, [savedFilters, setScope, clearScope]);

  return {
    scope,
    setScopeFromCustomer,
    setScopeFromFilter,
    clearScope,
  };
}

export default useScopeSync;
